/**
 * @fileoverview Table header configuration for Sunstate Urology's
 * "Not To Work - Appointments" worklist tab.
 *
 * This is an org-specific variant of the generic auth-status worklist headers
 * (`getDefaultAuthStatusTableHeaders`). It strips out the BO Value, Auth
 * Status, RPA Status, and RPA Status Two columns and replaces them with an
 * "Appointment Notes" column (rendered as `STRING_WITH_CUSTOM_TOOLTIP`,
 * backed by the `INSTRUCTIONS` column key) and a Location column. The Date
 * of Work column is swapped for a Date of Creation column. All other columns
 * mirror the default header set.
 *
 * @module sunstateNotToWorkAppointmentsTableHeaders
 */

import { TableCellType } from "../../../../components/custom-table/table";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Returns the table header configuration for the Sunstate Urology
 * "Not To Work - Appointments" worklist tab.
 *
 * @description
 * Differences from `getDefaultAuthStatusTableHeaders`:
 * - "Date of Work" column is replaced by "Date of Creation"
 *   (`CREATED_AT`, sortable on `created_at`).
 * - "BO Value", "Auth Status", "RPA Status", and "RPA Status Two" columns
 *   are removed.
 * - Two new columns are added in their place:
 *   - "Appointment Notes" (column key `INSTRUCTIONS`) rendered via
 *     `STRING_WITH_CUSTOM_TOOLTIP`; the underlying data must be shaped as
 *     `{ displayText, tooltipText }` (see
 *     `generateGenericAuthStatusTableDataFromAlgoliaRecords`). The column
 *     key remains `INSTRUCTIONS` for parity with the backing
 *     `algoliaRecord.instructions` field.
 *   - "Location" (`LOCATION`) rendered as a plain string.
 * - Column widths re-balanced to sum to 100%.
 *
 * Appointment Notes and Location are not server-sortable — neither field has
 * a corresponding entry in `MedicalPaOrdersAlgoliaSortFields`.
 *
 * @returns {Array<Object>} Array of table header configuration objects whose
 *   widths sum to 100
 */
export const getSunstateNotToWorkAppointmentsTableHeaders = () => {
  return [
    {
      label: "Patient Name",
      key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS,
      order: 1,
      width: 13,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
    },
    {
      label: "Date of Creation",
      key: GenericAuthStatusTableColumnKeys.CREATED_AT,
      order: 2,
      width: 12,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
    },
    {
      label: "Plan Details",
      key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
      order: 3,
      width: 9,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
    },
    {
      label: "Location",
      key: GenericAuthStatusTableColumnKeys.LOCATION,
      order: 4,
      width: 12,
      type: TableCellType.STRING,
    },
    {
      label: "Medication",
      key: GenericAuthStatusTableColumnKeys.MEDICATION,
      order: 5,
      width: 10,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
    },
    {
      label: "Provider",
      key: GenericAuthStatusTableColumnKeys.PROVIDER,
      order: 6,
      width: 13,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
    },
    {
      label: "DOS",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
      order: 7,
      width: 8,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
    },
    {
      label: "Appointment Notes",
      key: GenericAuthStatusTableColumnKeys.INSTRUCTIONS,
      order: 8,
      width: 16,
      type: TableCellType.STRING_WITH_CUSTOM_TOOLTIP,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
      order: 9,
      width: 7,
      type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
    },
  ];
};
